import { type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MdArrowBack } from 'react-icons/md';
import Spinners from '../../spinnners/Spinners';
import { formatStatusColor } from '../../../shared/helper/formatStatus';
import UserStorage from '../../../shared/utils/userStorage';
import { useGetEstateResidentsQuery } from '../../../redux/features/dashboard/dashboardApi';

const CommunityManagementDetail = () => {
  const { id } = useParams<{ id: string }>();

  const navigate = useNavigate();

  const community_id = UserStorage.getUserId() ?? '';

  const {
    data: residentData,
    isLoading,
    isError,
    refetch,
  } = useGetEstateResidentsQuery(
    { community_admin_id: community_id || '', resident_id: id ?? '' },
    { skip: !community_id },
  );

  const resident = residentData?.data?.data[0];

  if (isLoading) {
    return (
      <section>
        <div className="flex items-center justify-center min-h-100">
          <Spinners variant="default" size="xl" color="primary" />
        </div>
      </section>
    );
  }

  if (isError) {
    return (
      <div className="p-6">
        <div className="bg-white border border-red-200 rounded-lg p-8 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Failed to load resident details
          </h3>
          <button
            onClick={() => {
              refetch();
            }}
            className="mt-2 text-sm text-pry underline"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!resident) {
    return (
      <div className="p-6">
        <div className="bg-white border border-border rounded-lg p-8 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Resident not found
          </h3>
          <button
            onClick={() => navigate(-1)}
            className="mt-2 text-sm text-pry underline"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full mx-auto space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center justify-center h-8 w-8 rounded-full hover:bg-gray-100 transition-colors"
        >
          <MdArrowBack size={20} className="text-gray-600" />
        </button>
        <h2 className="text-xl font-semibold text-pry capitalize">
          {resident.resident.user.first_name}
          {resident.resident.user.last_name &&
            ` ${resident.resident.user.last_name}`}
        </h2>
      </div>

      {/* Street Info */}
      <div className="bg-white border border-border rounded-lg">
        <div className="px-6 py-4 border-b border-border">
          <h3 className="text-base font-medium text-pry-text">
            Resident Information
          </h3>
        </div>
        <div className="px-6 py-4 space-y-1">
          <InfoRow
            label="First Name"
            value={resident.resident.user.first_name}
          />
          <InfoRow label="Last Name" value={resident.resident.user.last_name} />
          <InfoRow
            label="Status"
            value={
              <span
                className={`text-sm font-medium ${formatStatusColor(resident.status)}`}
              >
                {resident.status}
              </span>
            }
          />
          <InfoRow
            label="Phone Number"
            value={resident.resident.user.mobile_number ?? 'N/A'}
          />
          <InfoRow
            label="Email"
            value={resident.resident.user.email ?? 'N/A'}
          />
        </div>
      </div>

      {/* Buildings Table */}
      <div className="bg-white border border-border rounded-lg">
        <div className="px-6 py-4 border-b border-border">
          <h3 className="text-base font-medium text-pry-text">
            Building Information
          </h3>
        </div>
        <div className="px-6 py-4 space-y-1">
          <InfoRow label="Street Name" value={resident.building.street.name} />
          <InfoRow
            label="Building number"
            value={resident.building.building_number}
          />
          <InfoRow
            label="Building Description"
            value={resident.building.description}
          />
        </div>
      </div>
    </div>
  );
};

const InfoRow = ({
  label,
  value,
}: {
  label: string;
  value: string | number | ReactNode;
}) => (
  <div className="flex py-3 border-b border-border last:border-0">
    <div className="w-40 text-sm text-gray-500 font-medium">{label}</div>
    <div className="flex-1 text-sm text-gray-900 font-medium capitalize">
      {value}
    </div>
  </div>
);

export default CommunityManagementDetail;
